import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import React, { useEffect, useMemo, useState } from 'react';

import type {
  ArgoApplication,
  ArgoResource,
  ArgoSyncRequest,
} from './types';
import { COLORS } from './ui';

const SYNC_OPTIONS = [
  ['Replace=true', 'Replace'],
  ['ServerSideApply=true', 'Server-side apply'],
  ['ApplyOutOfSyncOnly=true', 'Apply out of sync only'],
  ['RespectIgnoreDifferences=true', 'Respect ignore differences'],
  ['PruneLast=true', 'Prune last'],
] as const;

const flagSx = { '& .MuiTypography-root': { fontSize: 13 } };

function resourceId(resource: ArgoResource) {
  return [
    resource.group ?? '',
    resource.kind ?? '',
    resource.namespace ?? '',
    resource.name ?? '',
  ].join('/');
}

export function SyncPanel({
  application,
  resources,
  busy,
  onClose,
  onSubmit,
}: {
  application: ArgoApplication | null;
  resources: ArgoResource[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (request: ArgoSyncRequest) => void;
}) {
  const [revision, setRevision] = useState('');
  const [prune, setPrune] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [applyOnly, setApplyOnly] = useState(false);
  const [force, setForce] = useState(false);
  const [retry, setRetry] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [propagation, setPropagation] = useState('foreground');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const syncable = useMemo(
    () => resources.filter(resource => !resource.orphaned),
    [resources],
  );

  useEffect(() => {
    if (!application) return;
    setRevision(application.targetRevision ?? '');
    setPrune(false);
    setDryRun(false);
    setApplyOnly(false);
    setForce(false);
    setRetry(false);
    setOptions([]);
    setPropagation('foreground');
    setSelected(new Set(syncable.map(resourceId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application]);

  const allSelected = selected.size === syncable.length;
  const selectedResources = syncable.filter(resource =>
    selected.has(resourceId(resource)),
  );
  const pruneTargets = selectedResources.filter(
    resource => resource.requiresPruning,
  );

  const toggleOption = (option: string) =>
    setOptions(current =>
      current.includes(option)
        ? current.filter(value => value !== option)
        : [...current, option],
    );

  const submit = () =>
    onSubmit({
      revision: revision || undefined,
      prune,
      dryRun,
      force,
      applyOnly,
      syncOptions: [
        ...options,
        ...(propagation !== 'foreground'
          ? [`PrunePropagationPolicy=${propagation}`]
          : []),
      ],
      retryStrategy: retry
        ? {
            limit: 2,
            backoff: { duration: '5s', factor: 2, maxDuration: '3m0s' },
          }
        : undefined,
      resources: allSelected
        ? undefined
        : selectedResources.map(resource => ({
            group: resource.group ?? '',
            kind: resource.kind,
            name: resource.name,
            namespace: resource.namespace,
          })),
    });

  return (
    <Dialog
      open={Boolean(application)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      {application && (
        <>
          <DialogTitle>
            Sync {application.name}
            <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
              {application.instance} · {application.project}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              size="small"
              label="Revision"
              value={revision}
              onChange={event => setRevision(event.target.value)}
              sx={{ mt: 0.5, mb: 1.5 }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 1,
              }}
            >
              <FormControlLabel
                sx={flagSx}
                control={
                  <Checkbox
                    size="small"
                    checked={prune}
                    onChange={event => setPrune(event.target.checked)}
                  />
                }
                label="Prune"
              />
              <FormControlLabel
                sx={flagSx}
                control={
                  <Checkbox
                    size="small"
                    checked={dryRun}
                    onChange={event => setDryRun(event.target.checked)}
                  />
                }
                label="Dry run"
              />
              <FormControlLabel
                sx={flagSx}
                control={
                  <Checkbox
                    size="small"
                    checked={applyOnly}
                    onChange={event => setApplyOnly(event.target.checked)}
                  />
                }
                label="Apply only"
              />
              <FormControlLabel
                sx={flagSx}
                control={
                  <Checkbox
                    size="small"
                    checked={force}
                    onChange={event => setForce(event.target.checked)}
                  />
                }
                label="Force"
              />
              <FormControlLabel
                sx={flagSx}
                control={
                  <Checkbox
                    size="small"
                    checked={retry}
                    onChange={event => setRetry(event.target.checked)}
                  />
                }
                label="Retry (2x, backoff)"
              />
            </Box>

            <Typography
              sx={{
                mt: 1.5,
                mb: 0.5,
                color: COLORS.muted,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              SYNC OPTIONS
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 1,
              }}
            >
              {SYNC_OPTIONS.map(([value, label]) => (
                <FormControlLabel
                  key={value}
                  sx={flagSx}
                  control={
                    <Checkbox
                      size="small"
                      checked={options.includes(value)}
                      onChange={() => toggleOption(value)}
                    />
                  }
                  label={label}
                />
              ))}
            </Box>
            <TextField
              select
              size="small"
              label="Prune propagation policy"
              value={propagation}
              onChange={event => setPropagation(event.target.value)}
              sx={{ mt: 1, width: 240 }}
            >
              <MenuItem value="foreground">foreground</MenuItem>
              <MenuItem value="background">background</MenuItem>
              <MenuItem value="orphan">orphan</MenuItem>
            </TextField>

            {force && options.includes('Replace=true') && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Force + Replace deletes and recreates resources — workloads may
                briefly disappear.
              </Alert>
            )}
            {prune && pruneTargets.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {pruneTargets.length} resource
                {pruneTargets.length === 1 ? '' : 's'} will be deleted:{' '}
                {pruneTargets
                  .slice(0, 5)
                  .map(resource => `${resource.kind}/${resource.name}`)
                  .join(', ')}
                {pruneTargets.length > 5 ? ', …' : ''}
              </Alert>
            )}

            {syncable.length > 0 && (
              <>
                <Stack
                  direction="row"
                  sx={{ mt: 1.5, mb: 0.5, alignItems: 'center', gap: 1 }}
                >
                  <Typography
                    sx={{ color: COLORS.muted, fontSize: 11, fontWeight: 700 }}
                  >
                    RESOURCES ({selected.size}/{syncable.length})
                  </Typography>
                  <Button
                    size="small"
                    sx={{ fontSize: 11, textTransform: 'none' }}
                    onClick={() =>
                      setSelected(new Set(syncable.map(resourceId)))
                    }
                  >
                    All
                  </Button>
                  <Button
                    size="small"
                    sx={{ fontSize: 11, textTransform: 'none' }}
                    onClick={() =>
                      setSelected(
                        new Set(
                          syncable
                            .filter(resource => resource.status === 'OutOfSync')
                            .map(resourceId),
                        ),
                      )
                    }
                  >
                    Out of sync
                  </Button>
                  <Button
                    size="small"
                    sx={{ fontSize: 11, textTransform: 'none' }}
                    onClick={() => setSelected(new Set())}
                  >
                    None
                  </Button>
                </Stack>
                <Box
                  sx={{
                    maxHeight: 220,
                    overflow: 'auto',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 1,
                    px: 1,
                  }}
                >
                  {syncable.map(resource => {
                    const id = resourceId(resource);
                    return (
                      <FormControlLabel
                        key={id}
                        sx={{ display: 'flex', ...flagSx }}
                        control={
                          <Checkbox
                            size="small"
                            checked={selected.has(id)}
                            onChange={event =>
                              setSelected(current => {
                                const next = new Set(current);
                                if (event.target.checked) next.add(id);
                                else next.delete(id);
                                return next;
                              })
                            }
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: 12 }}>
                            {resource.kind}/{resource.name}
                            <Typography
                              component="span"
                              sx={{ ml: 0.75, color: COLORS.muted, fontSize: 11 }}
                            >
                              {resource.status ?? ''}
                              {resource.requiresPruning ? ' · prune' : ''}
                            </Typography>
                          </Typography>
                        }
                      />
                    );
                  })}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={busy || (!allSelected && selected.size === 0)}
              startIcon={
                busy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SyncIcon />
                )
              }
              onClick={submit}
            >
              {dryRun ? 'Dry run' : 'Synchronize'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
